import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Card,
    Collapse,
    Dialog,
    IconButton,
    List,
    ListItem,
    Typography,
    ButtonBase,
    TextField,
    Tooltip
} from "@mui/material";
import {ArrowBack, ArrowDropDown, ArrowDropUp, Close} from "@material-ui/icons";
import RemoveIcon from "@material-ui/icons/Remove";
import useStyles from '../../../assets/styles/ProgramOutlineStyles';
import {useHistory, useLocation, useParams} from "react-router-dom";
import {DQC_COLORS, KSAB_COLOR_MAPPING, LOCAL_STORAGE_KEYS} from "../../../utils/constants";
import {
    generateLearningProgramContentService,
    getInsiderProgramService,
    getLearningProgramPreviewService,
    listV3Level7DQCodeService,
    updateLearningProgramService,
    getActiveChatInsiderProgramService,
    clearChatStateInsiderService,
    removeModuleFromProgramService,
    removeUnitFromModuleService,
    removeSessionFromUnitService
} from "../../../services/learning_programs.service";
import LoaderWithBackDrop from "../../../components/LoaderWithBackDrop/LoaderWithBackDrop.comp";
import {toastWarning} from "../../../utils/utils";
import EditIcon from "@material-ui/icons/Edit";
import OutlinePdfDoc from "./components/OutlinePdfDoc.comp";
import PrintIcon from "@material-ui/icons/Print";
import { pdf } from '@react-pdf/renderer';

const TARGET_GROUP = {
    General: "General Public",
    Children: "Children",
    Teens: "Teenagers",
};

const TARGET_AGE = {
    "8-9YO": "Ages 8-9",
    "10-12YO": "Ages 10-12",
    "13-14YO": "Ages 13-14",
    "15-18YO": "Ages 15-18",
};

const DQProProgOutline = () => {
    const classes = useStyles();
    const { code: dq_module_code } = useParams();
    const location = useLocation();
    const history = useHistory();
    const [outlineStatus, setOutlineStatus] = useState(
        location.state?.outlineStatus || {
            is_outline_ready: null,
            generation_initiated: null
        }
    );
    const [programData, setProgramData] = useState(location.state?.assessment || null);
    const [programDuration, setProgramDuration] = useState( 0);
    const [programAudience, setProgramAudience] = useState('');
    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(true);

    const [expandedUnits, setExpandedUnits] = useState([]);
    const [expandedModules, setExpandedModules] = useState([]);
    const [expandedSessions, setExpandedSessions] = useState({});
    const [hasInitializedExpansion, setHasInitializedExpansion] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [itemToRemove, setItemToRemove] = useState({ type: null, m_id: null, u_id: null, s_id: null });
    const [popupContent, setPopupContent] = useState(null);
    const [isEducatorView, setIsEducatorView] = useState(true);
    const [dqcoList, setDqcoList] = useState([]);
    const [isEditingDescription, setIsEditingDescription] = useState(false);
    const [editedDescription, setEditedDescription] = useState('');
    const [isEditingProgramName, setIsEditingProgramName] = useState(false);
    const [openStartOverDialog, setOpenStartOverDialog] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [openLastItemDialog, setOpenLastItemDialog] = useState(false);
    const [lastItemToRemove, setLastItemToRemove] = useState({ type: null, m_id: null, u_id: null, s_id: null });
    const [willDeleteParent, setWillDeleteParent] = useState(null); // 'program', 'module', or 'unit'
    const [openNewProgramDialog, setOpenNewProgramDialog] = useState(false);
    const [frameAlignedContent, setFrameAlignedContent] = useState();
    const [openGenerateConfirmDialog, setOpenGenerateConfirmDialog] = useState(false);

    useEffect(() => {
        const fetchProgramData = async () => {
            try {
                if (!programData && dq_module_code) {
                    setLoading(true);
                    const response = await getInsiderProgramService(dq_module_code);
                    if (response?.data?.learning_program) {
                        setProgramData(response.data.learning_program);
                        console.log('program: ', response.data.learning_program);
                    }
                }
            } catch (error) {
                console.error('Error fetching program data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchProgramData().then(r => console.log(r));
    }, [programData, dq_module_code]);

    useEffect(() => {
        const fetchModules = async () => {
            if (!dq_module_code) {
                setModules([]);
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const res = await getLearningProgramPreviewService(dq_module_code);
                if (!res?.data?.is_outline_ready) {
                    toastWarning('Learning program outline not ready yet!');
                    setLoading(false);
                    return;
                }
                if (res?.data?.program_layout_preview) {
                    console.log('outline: ', res.data.program_layout_preview)
                    const program = res?.data?.program_layout_preview || {};
                    if (!program || !Array.isArray(program.modules)) {
                        setModules([]);
                        setLoading(false);
                        return;
                    }

                    setProgramDuration(program.total_duration_minutes);
                    setProgramAudience(program.target_age_group);
                    // setProgramData(program);

                    const mappedModules = program.modules.map(mod => ({
                        module_code: mod.module_code || '',
                        module_name: mod.module_title || '',
                        module_educator_description: mod.description_for_educator || '',
                        module_learner_description: mod.description_for_learner || '',
                        module_duration: mod.total_duration_minutes || '',
                        module_items_definitions: {
                            title: mod.definition_title || '',
                            description: mod.definition || ''
                        },
                        unique_dqco: mod.unique_dqco || [],
                        unique_frame_arc: mod.unique_frame_arc || [],
                        units: (mod.units || []).map(unit => ({
                            unit_code: unit.unit_code || '',
                            unit_title: unit.unit_title || '',
                            unit_educator_description: unit.description_for_educator || '',
                            unit_learner_description: unit.description_for_learner || '',
                            unit_duration: unit.total_duration_minutes || 0,
                            lessons: (unit.sessions || []).map(session => ({
                                lesson_title: session.session_title || '',
                                lesson_duration: session.duration_minutes || 0,
                                lesson_educator_description: session.description_for_educator || '',
                                lesson_learner_description: session.description_for_learner || '',
                                session_learning_objectives: session.session_learning_objectives || [],
                                session_format: session.session_format || '',
                                session_unique_ksab: session.unique_ksab || [],
                                session_code: session.session_code || '',
                            })),
                        })),
                    }));

                    setModules(mappedModules);
                }
            } catch (err) {
                console.error('Error fetching modules:', err);
                setModules([]);
            } finally {
                setLoading(false);
            }
        };

        fetchModules().then(r => console.log(r));
    }, [dq_module_code]);

    useEffect(() => {
        if (dqcoList.length === 0) fetchV3Level7DQCodeList();
        // eslint-disable-next-line
    }, []);

    // Initialize module expansion when modules are loaded
    useEffect(() => {
        if (modules.length > 0 && !hasInitializedExpansion) {
            // Expand all modules by default
            setExpandedModules(modules.map((_, index) => index));
            setHasInitializedExpansion(true);
        }
    }, [modules, hasInitializedExpansion]);

    const handleEditDescription = () => {
        setEditedDescription(programData?.description || '');
        setIsEditingDescription(true);
    };

    const handleCancelEdit = () => {
        setIsEditingDescription(false);
        setEditedDescription('');
    };

    const handleEditProgramName = () => {
        setIsEditingProgramName(true);
    };

    const handleCancelEditProgramName = () => {
        setIsEditingProgramName(false);
    };

    const handleSaveDescription = async () => {
        try {
            setLoading(true);
            const payload = {
                description: editedDescription
            };

            const response = await updateLearningProgramService(dq_module_code, payload);

            if (response && response.data) {
                // Update local state with new description
                setProgramData(prev => ({
                    ...prev,
                    description: editedDescription
                }));

                setIsEditingDescription(false);
                setEditedDescription('');

                // Optional: Show success message
                // toastSuccess('Description updated successfully');
            } else {
                toastWarning('Failed to update description');
            }
        } catch (error) {
            console.error('Error updating description:', error);
            toastWarning('Failed to update description');
        } finally {
            setLoading(false);
        }
    };

    const handleSaveProgramName = async () => {
        try {
            setLoading(true);
            const payload = {
                full_name: programData?.full_name
            };

            const response = await updateLearningProgramService(dq_module_code, payload);

            if (response && response.data) {
                // Update local state with new program name
                setProgramData(prev => ({
                    ...prev,
                    full_name: programData?.full_name
                }));

                setIsEditingProgramName(false);
            } else {
                toastWarning('Failed to update program name');
            }
        } catch (error) {
            toastWarning('Failed to update program name');
        } finally {
            setLoading(false);
        }
    };

    const toggleUnit = (unitCode) => {
        setExpandedUnits((prev) =>
            prev.includes(unitCode) ? prev.filter(code => code !== unitCode) : [...prev, unitCode]
        );
    };

    const toggleModule = (moduleIndex) => {
        setExpandedModules((prev) =>
            prev.includes(moduleIndex) ? prev.filter(i => i !== moduleIndex) : [...prev, moduleIndex]
        );
    };

    const toggleSession = (sessionCode) => {
        setExpandedSessions((prev) => ({
            ...prev,
            [sessionCode]: !prev[sessionCode],
        }));
    };

    const fetchV3Level7DQCodeList = async () => {
        try {
            const res = await listV3Level7DQCodeService();
            if (res?.data?.metaData) {
                setDqcoList(res.data.metaData);
            }
        } catch (err) {
            // Optionally handle error
            setDqcoList([]);
        }
    };

    // Unified function to refetch all program data
    const refetchProgramData = async () => {
        if (!dq_module_code) {
            setModules([]);
            return;
        }

        try {
            setLoading(true);

            // Fetch updated program info
            const programResponse = await getInsiderProgramService(dq_module_code);
            if (programResponse?.data?.learning_program) {
                setProgramData(programResponse.data.learning_program);
            }

            // Fetch updated modules and program structure
            const res = await getLearningProgramPreviewService(dq_module_code);
            if (!res?.data?.is_outline_ready) {
                toastWarning('Learning program outline not ready yet!');
                return;
            }

            if (res?.data?.program_layout_preview) {
                const program = res?.data?.program_layout_preview || {};
                if (!program || !Array.isArray(program.modules)) {
                    setModules([]);
                    return;
                }

                setProgramDuration(program.total_duration_minutes);
                setProgramAudience(program.target_age_group);

                const mappedModules = program.modules.map(mod => ({
                    module_code: mod.module_code || '',
                    module_name: mod.module_title || '',
                    module_educator_description: mod.description_for_educator || '',
                    module_learner_description: mod.description_for_learner || '',
                    module_duration: mod.total_duration_minutes || '',
                    module_items_definitions: {
                        title: mod.definition_title || '',
                        description: mod.definition || ''
                    },
                    unique_dqco: mod.unique_dqco || [],
                    unique_frame_arc: mod.unique_frame_arc || [],
                    units: (mod.units || []).map(unit => ({
                        unit_code: unit.unit_code || '',
                        unit_title: unit.unit_title || '',
                        unit_educator_description: unit.description_for_educator || '',
                        unit_learner_description: unit.description_for_learner || '',
                        unit_duration: unit.total_duration_minutes || 0,
                        lessons: (unit.sessions || []).map(session => ({
                            lesson_title: session.session_title || '',
                            lesson_duration: session.duration_minutes || 0,
                            lesson_educator_description: session.description_for_educator || '',
                            lesson_learner_description: session.description_for_learner || '',
                            session_learning_objectives: session.session_learning_objectives || [],
                            session_format: session.session_format || '',
                            session_unique_ksab: session.unique_ksab || [],
                            session_code: session.session_code || '',
                        })),
                    })),
                }));

                setModules(mappedModules);
            }
        } catch (err) {
            console.error('Error refetching program data:', err);
            setModules([]);
        } finally {
            setLoading(false);
        }
    };

    // Helper functions to check if item is the last one
    const isLastModule = () => {
        return modules.length === 1;
    };

    const isLastUnit = (moduleCode) => {
        const module = modules.find(m => m.module_code === moduleCode);
        return module && module.units.length === 1;
    };

    const isLastSession = (moduleCode, unitCode) => {
        const module = modules.find(m => m.module_code === moduleCode);
        if (!module) return false;
        const unit = module.units.find(u => u.unit_code === unitCode);
        return unit && unit.lessons.length === 1;
    };

    const checkIfLastItem = (type, m_id, u_id, s_id) => {
        if (type === 'module' && isLastModule()) {
            return true;
        }
        if (type === 'unit' && isLastUnit(m_id)) {
            return true;
        }
        if (type === 'session' && isLastSession(m_id, u_id)) {
            return true;
        }
        return false;
    };

    const handleModuleCodeClick = async (module, dqco) => {
        // If dqcoList is empty, fetch it
        if (dqcoList.length === 0) {
            await fetchV3Level7DQCodeList();
        }

        const dqcoObj = dqcoList.find(item => item.dqco_no === dqco);

        setPopupContent({
            code: dqco,
            dqc_no: dqcoObj?.dqc_no,
            title: dqcoObj?.full_name,
            description: dqcoObj?.definition
        });
    };

    const handleModuleFrameworkAlignedClick = async (module) => {
        // Ensure we always show the default framework at the top of the list
        const DEFAULT_FRAMEWORK_NAME = 'IEEE DQ Global Standards (IEEE3527.1)';
        const defaultEntry = {
            FrameName: DEFAULT_FRAMEWORK_NAME,
            "SourceLink (same as ProgL2)": 'https://www.dqinstitute.org/global-standards/'
        };

        const list = Array.isArray(module.unique_frame_arc) ? [...module.unique_frame_arc] : [];

        const filtered = list.filter(f => (f?.FrameName || '').trim() !== DEFAULT_FRAMEWORK_NAME);
        const sorted = filtered.sort((a, b) => {
            const aName = String(a?.FrameName ?? '').toLowerCase();
            const bName = String(b?.FrameName ?? '').toLowerCase();
            return aName.localeCompare(bName);
        });

        setFrameAlignedContent([defaultEntry, ...sorted]);
    };


    const handleRemoveItem = (type, m_id, u_id, s_id) => {
        console.log("Remove item triggered:", { type, m_id, u_id, s_id });

        // Check what will happen when removing this item with full cascading logic
        let willDeleteParent = null;

        if (type === 'module' && isLastModule()) {
            willDeleteParent = 'program';
        } else if (type === 'unit') {
            // Check if removing this unit will delete the module, and if that module is the last one
            if (isLastUnit(m_id)) {
                if (isLastModule()) {
                    willDeleteParent = 'program';
                } else {
                    willDeleteParent = 'module';
                }
            }
        } else if (type === 'session') {
            // Check cascading effects: session -> unit -> module -> program
            if (isLastSession(m_id, u_id)) {
                if (isLastUnit(m_id)) {
                    if (isLastModule()) {
                        willDeleteParent = 'program';
                    } else {
                        willDeleteParent = 'module';
                    }
                } else {
                    willDeleteParent = 'unit';
                }
            }
        }

        if (willDeleteParent) {
            setLastItemToRemove({ type, m_id, u_id, s_id });
            setWillDeleteParent(willDeleteParent);
            setOpenLastItemDialog(true);
        } else {
            setItemToRemove({ type, m_id, u_id, s_id });
            setOpenDialog(true);
        }
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setItemToRemove({ type: null, m_id: null, u_id: null, s_id: null });
    };

    const handleConfirmRemove = async () => {
        const { type, m_id, u_id, s_id } = itemToRemove;

        try {
            setLoading(true);

            if (type === 'module') {
                const response = await removeModuleFromProgramService(dq_module_code, m_id);
                if (response && response.data) {
                    // Refetch all program data to get updated information
                    await refetchProgramData();
                } else {
                    toastWarning('Failed to remove module');
                    setLoading(false);
                    handleCloseDialog();
                    return;
                }
            }
            else if (type === 'unit') {
                const response = await removeUnitFromModuleService(dq_module_code, m_id, u_id);
                if (response && response.data) {
                    // Refetch all program data to get updated information
                    await refetchProgramData();
                } else {
                    toastWarning('Failed to remove unit');
                    setLoading(false);
                    handleCloseDialog();
                    return;
                }
            }
            else if (type === 'session') {
                const response = await removeSessionFromUnitService(dq_module_code, m_id, u_id, s_id);
                if (response && response.data) {
                    // Refetch all program data to get updated information
                    await refetchProgramData();
                } else {
                    toastWarning('Failed to remove session');
                    setLoading(false);
                    handleCloseDialog();
                    return;
                }
            }

            // Reset UI states after successful removal
            // setExpandedModules([]);
            // setExpandedUnits([]);
            // setExpandedSessions({});

        } catch (error) {
            console.error(`Error removing ${type}:`, error);
            toastWarning(`Failed to remove ${type}`);
        } finally {
            setLoading(false);
            handleCloseDialog();
        }
    };

    const handleGenerateProgram = async () => {
        const request_type = 'new'
        try {
            setLoading(true);
            const response = await generateLearningProgramContentService(dq_module_code, request_type);
            if (response && response.data?.initiated === true) {
                history.push(`/dq_pro/products/program/dashboard/${dq_module_code}`, dqcoList);
            } else {
                toastWarning(response?.error_message || 'Failed to generate program content');
            }
        } catch (error) {
            console.error('Error generating program:', error);
            toastWarning('Failed to generate program content');
        } finally {
            setLoading(false);
        }
    };

    const handleGenerateClick = () => {
        // Check if it's the first free program - skip dialog if true
        const isFirstFree = programData?.is_first_free || false;
        
        if (isFirstFree) {
            // Skip confirmation for first free program
            handleGenerateProgram();
        } else {
            // Show confirmation dialog
            setOpenGenerateConfirmDialog(true);
        }
    };

    const calculateTotalSessions = () => {
        return modules.reduce((total, module) => {
            return total + (module.units || []).reduce((unitTotal, unit) => {
                return unitTotal + (unit.lessons || []).length;
            }, 0);
        }, 0);
    };

    const handleStartOver = async () => {
        console.log("Starting program", programData);
        try {
            if(programData.code) {
                const response = await clearChatStateInsiderService(programData.code);
                console.log('Clear chat state response:', response);
                if (response.data.success) {
                    history.push('/dq_pro/program/creation');
                }
            }
        } catch (e) {
            console.warn('Failed to clear server chat state:', e);
        }
    };

    const handleCloseLastItemDialog = () => {
        setOpenLastItemDialog(false);
        setLastItemToRemove({ type: null, m_id: null, u_id: null, s_id: null });
        setWillDeleteParent(null);
    };

    const handleConfirmLastItemRemoval = async () => {
        const { type, m_id, u_id, s_id } = lastItemToRemove;

        try {
            setLoading(true);

            if (willDeleteParent === 'program') {
                // If removing last module, delete entire program
                await handleStartOver();
                return;
            } else if (willDeleteParent === 'module') {
                // If removing last unit from module, delete the module
                const response = await removeModuleFromProgramService(dq_module_code, m_id);
                if (response && response.data) {
                    await refetchProgramData();
                } else {
                    toastWarning('Failed to remove module');
                }
            } else if (willDeleteParent === 'unit') {
                // If removing last session from unit, delete the unit
                const response = await removeUnitFromModuleService(dq_module_code, m_id, u_id);
                if (response && response.data) {
                    await refetchProgramData();
                } else {
                    toastWarning('Failed to remove unit');
                }
            }

            // Reset UI states after successful removal
            // setExpandedModules([]);
            // setExpandedUnits([]);
            // setExpandedSessions({});

        } catch (error) {
            console.error(`Error handling last item removal:`, error);
            toastWarning(`Failed to complete removal operation`);
        } finally {
            setLoading(false);
            handleCloseLastItemDialog();
        }
    };

    const handleCloseNewProgramDialog = () => {
        setOpenNewProgramDialog(false);
        setLastItemToRemove({ type: null, m_id: null, u_id: null, s_id: null });
    };

    const getAudienceText = () => {
        const group_part = TARGET_GROUP[programData?.target_audience] || programData?.target_audience;
        const age_part = TARGET_AGE[programData?.target_age] || 'All Ages';
        return `${group_part}`;
    };

    const { totalModules, totalUnits, totalSessions } = modules.reduce((acc, module) => {
        acc.totalModules += 1;
        acc.totalUnits += module.units.length;
        acc.totalSessions += module.units.reduce((unitTotal, unit) => {
            return unitTotal + (unit.lessons ? unit.lessons.length : 0);
        }, 0);
        return acc;
    }, { totalModules: 0, totalUnits: 0, totalSessions: 0 });


    const CustomToggle = ({ isEducatorView, setIsEducatorView }) => {
        return (
            <Box
                sx={{
                    display: 'flex',
                    borderRadius: '20px',
                    overflow: 'hidden',
                    border: '2px solid #ff6468',
                    width: {xs:'116px', sm:'155px'},
                    height: '38px',
                    backgroundColor: '#ff6468',
                    marginLeft:'auto',
                    cursor: 'pointer',
                }}
            >
                <ButtonBase
                    onClick={() => setIsEducatorView(true)}
                    sx={{
                        flex: 1,
                        backgroundColor: isEducatorView ?  '#fff' : '#ff6468',
                        color: isEducatorView ?'#ff6468' : '#fff',
                        borderRadius:'20px',
                        fontWeight: 'bold',
                        fontSize: {xs:'12px', sm:'14px'},
                        height: '100%',
                        transition: 'all 0.3s ease',
                    }}
                >
                    Educator
                </ButtonBase>
                <ButtonBase
                    onClick={() => setIsEducatorView(false)}
                    sx={{
                        flex: 1,
                        backgroundColor: !isEducatorView ?  '#fff' : '#ff6468',
                        color: !isEducatorView ? '#ff6468' : '#fff',
                        fontWeight: 'bold',
                        borderRadius:'20px',
                        fontSize: '14px',
                        height: '100%',
                        transition: 'all 0.3s ease',
                    }}
                >
                    Learner
                </ButtonBase>
            </Box>
        );
    };


    const handleOpenPdfInNewTab = async () => {
        if (pdfLoading) return;
        try {
            setPdfLoading(true);
            const doc = (
                <OutlinePdfDoc
                    programData={programData}
                    programAudience={programAudience}
                    programDuration={programDuration}
                    modules={modules}
                    isEducatorView={isEducatorView}
                    dqcoList={dqcoList}
                />
            );
            const blob = await pdf(doc).toBlob();
            const url = URL.createObjectURL(blob);
            window.open(url, '_blank', 'noopener');
            // Revoke after some time to allow loading
            setTimeout(() => URL.revokeObjectURL(url), 120000);
        } catch (e) {
            console.error('Failed to generate PDF', e);
            toastWarning('Failed to open PDF');
        } finally {
            setPdfLoading(false);
        }
    };

    const handleBack = () => {
        history.push(`/dq_pro/products/program/dashboard/${dq_module_code}`);
    };

    return (
        <>
            <Box sx={{ minHeight: { xs: '95vh', sm: '87vh' }, mt:{sm:'75px', lg:'auto'}, width: { xs: '100vw', sm: '100%' } }} style={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'space-between',
            }}>
                <IconButton
                    onClick={handleBack}
                    sx={{
                        position: 'absolute',
                        top: { xs: 16, md: 24 },
                        left: { xs: 16, md: 24 },
                        zIndex: 10,
                        width: 48,
                        height: 48,
                        borderRadius: '50%',
                        backgroundColor: '#ffffff',
                        color: '#0097b2',
                        boxShadow: '0 6px 18px rgba(0,0,0,0.18)',
                        border: '1px solid rgba(0,151,178,0.15)',
                        transition: 'all 0.25s ease',
                    }}
                >
                    <ArrowBack />
                </IconButton>
                <div className={classes.container}>
                <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    width="91%"
                    mb={5}
                >
                    {isEditingProgramName ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '70%', maxWidth: '600px' }}>
                            <TextField
                                value={programData?.full_name || ''}
                                onChange={(e) => setProgramData(prev => ({ ...prev, full_name: e.target.value }))}
                                variant="outlined"
                                size="small"
                                placeholder="Enter program name"
                                sx={{ 
                                    '& .MuiOutlinedInput-root': {
                                        fontWeight: '500 !important',
                                        fontSize: '1.3rem',
                                        '& input': {
                                            background: 'linear-gradient(to right, #6A8EC9 10%, #8C74A7 35%, #D44C71 59%, #C91C6E 100%)',
                                            backgroundClip: 'text',
                                            WebkitBackgroundClip: 'text',
                                            WebkitTextFillColor: 'transparent',
                                            color: 'transparent',
                                        },
                                        '& fieldset': {
                                            borderColor: '#ED6628',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: '#ED6628',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: '#ED6628',
                                        },
                                    }
                                }}
                            />
                            <Box sx={{ display: 'flex', gap: 1 }}>
                                <Button
                                    variant="contained"
                                    onClick={handleSaveProgramName}
                                    disabled={loading || !programData?.full_name?.trim()}
                                    size="small"
                                    sx={{
                                        backgroundColor: '#ED6628',
                                        '&:hover': { backgroundColor: '#ED6628' }
                                    }}
                                >
                                    {loading ? 'Saving...' : 'Save'}
                                </Button>
                                <Button
                                    variant="outlined"
                                    onClick={handleCancelEditProgramName}
                                    size="small"
                                    sx={{ color: '#ED6628', borderColor: '#ED6628' }}
                                >
                                    Cancel
                                </Button>
                            </Box>
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h4" className={classes.title} sx={{ mb: 0 }}>
                                {programData?.full_name} Program Outline
                            </Typography>
                            <Tooltip title="Edit program name">
                                <IconButton
                                    aria-label="edit program name"
                                    size="small"
                                    style={{ color: '#ED6628' }}
                                    onClick={handleEditProgramName}
                                >
                                    <EditIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}

                    <Box sx={{display:'flex',flexDirection:{xs:'column', sm:'row'}}}>
                    <CustomToggle isEducatorView={isEducatorView} setIsEducatorView={setIsEducatorView} />
                        <Tooltip title="Download">
                            <IconButton
                                aria-label="print outline"
                                onClick={handleOpenPdfInNewTab}
                                disabled={pdfLoading || !modules.length}
                                sx={{ mr:2, color:'#ff6468', borderRadius:'12px', p:1.2, opacity: (pdfLoading||!modules.length)?0.6:1 }}
                            >
                              {pdfLoading ? <span style={{fontSize:10,fontWeight:600}}>...</span> : <PrintIcon />}
                            </IconButton>
                        </Tooltip>
                    </Box>

                </Box>
                    <Box display="flex" alignItems="center" gap={1} mb={5}>
                        {/*<span*/}
                        {/*    style={{*/}
                        {/*        backgroundColor: '#D2DD2C',*/}
                        {/*        color: '#ffffff',*/}
                        {/*        borderRadius: '20px',*/}
                        {/*        fontSize: '12px',*/}
                        {/*        padding: '2px 10px',*/}
                        {/*        fontWeight: 500,*/}
                        {/*        display: 'inline-block',*/}
                        {/*        lineHeight: 1.5,*/}
                        {/*    }}*/}
                        {/*>*/}
                        {/*    DQ07*/}
                        {/*  </span>*/}
                    </Box>

                    <div className={classes.cardsContainer}>
                        <Card className={classes.card}>
                            <Typography variant="h6">Learners</Typography>
                            <Typography variant="subtitle1">{TARGET_AGE[programAudience] || programAudience}</Typography>
                        </Card>
                        <Card className={classes.card}>
                            <Typography variant="h6">Program Structure</Typography>
                            <Typography variant="subtitle1">{totalModules} Module(s) / {totalUnits} Unit(s) / {totalSessions} Session(s)</Typography>
                        </Card>
                        <Card className={classes.card}>
                            <Typography variant="h6">Program Length</Typography>
                            <Typography variant="subtitle1">{programDuration} minutes</Typography>
                        </Card>
                    </div>
                    <div className={classes.overviewContainer}>
                        <Typography variant="h6" style={{ color: '#ED6628', marginRight: '10px' }}
                                    className={classes.overview}>
                            <b>Overview:</b>
                        </Typography>

                        {isEditingDescription ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                                <TextField
                                    multiline
                                    rows={4}
                                    value={editedDescription}
                                    onChange={(e) => setEditedDescription(e.target.value)}
                                    variant="outlined"
                                    fullWidth
                                    sx={{ mt: 1 }}
                                />
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Button
                                        variant="contained"
                                        onClick={handleSaveDescription}
                                        disabled={loading}
                                        sx={{
                                            backgroundColor: '#ED6628',
                                            '&:hover': { backgroundColor: '#d5571f' }
                                        }}
                                    >
                                        Save
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        onClick={handleCancelEdit}
                                        sx={{ color: '#ED6628', borderColor: '#ED6628' }}
                                    >
                                        Cancel
                                    </Button>
                                </Box>
                            </Box>
                        ) : (
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', width: '100%' }}>
                                <Typography variant="body1" style={{ marginTop: '5px', flex: 1 }}
                                            className={classes.overview}>
                                    {programData?.description}
                                </Typography>
                                <Tooltip title="Edit">
                                    <IconButton
                                        aria-label="edit description"
                                        size="small"
                                        style={{ marginLeft: 8, color: '#ED6628' }}
                                        onClick={handleEditDescription}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}
                    </div>
                    <div className={classes.unitContainer}>
                        <Box sx={{ display: 'flex', gap: 2,marginLeft:'auto', marginRight:'auto', flexWrap: 'wrap', mb: 2 }}>
                            {Object.entries(KSAB_COLOR_MAPPING).map(([key, color]) => (
                                <Box key={key} sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    <Box
                                        sx={{
                                            width: 18,
                                            height: 18,
                                            borderRadius: '50%',
                                            backgroundColor: color,
                                        }}
                                    />
                                    <Typography variant="body1" sx={{ color: '#444' }}>
                                        {key}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>

                        {modules.map((module, moduleIndex) => (
                            <div key={module.module_code} className={classes.unit} style={{ width: '100%' }}>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'flex-start',
                                    flexDirection:'column',
                                    width: '100%',
                                    justifyContent: 'space-between'
                                }}>
                                <div style={{display: 'flex', flexDirection: 'column', width: '100%' }}>
                                    <Box style={{display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between'}}>
                                    <Button
                                        onClick={() => toggleModule(moduleIndex)}
                                        style={{
                                            color: '#EE6628',
                                            fontWeight: 'bold',
                                            outline: 'none',
                                            boxShadow: 'none',
                                            fontSize: '18px',
                                            textAlign: 'left',
                                            justifyContent: 'space-between',
                                            display: 'flex',
                                            padding: 0,
                                            width: '100%',
                                        }}
                                    >
                                            <Typography variant="h6" sx={{textTransform:'none',fontSize: {xs:'18px', sm:'1.25rem !important',}}}>
                                                {`Module ${moduleIndex + 1}: ${module.module_name}`}  ({module.module_duration} MINUTES TOTAL)
                                            </Typography>

                                            {expandedModules.includes(moduleIndex) ?
                                                (
                                                    <Tooltip title="Collapse to hide details">
                                                        <ArrowDropUp />
                                                    </Tooltip>
                                                ) : (
                                                    <Tooltip title="Expand to view details">
                                                        <ArrowDropDown />
                                                    </Tooltip>
                                                )}
                                            </Button>

                                            {!outlineStatus.generation_initiated && (
                                              <Tooltip title="Remove this item">
                                                <IconButton
                                                    onClick={() => handleRemoveItem('module', module.module_code)}
                                                    style={{ color: '#EE6628', marginLeft: '10px' }}
                                                >
                                                    <RemoveIcon />
                                                </IconButton>
                                              </Tooltip>
                                            )}
                                        </Box>

                                        {/* Tags row */}
                                        <Box mt={1} display="flex" flexWrap="wrap" gap={1}  mb='2'>
                                            {module.unique_dqco?.map((dqco, idx) => {
                                                const dqcoObj = dqcoList.find(item => item.dqco_no === dqco);
                                                return (
                                                    <Typography
                                                        key={dqco || idx}
                                                        sx={{
                                                            backgroundColor: DQC_COLORS[dqcoObj?.dqc_no] || '#D2DD2C',
                                                            color: '#ffffff',
                                                            borderRadius: '20px',
                                                            fontSize: {xs:'10px', sm:'16px'},
                                                            padding: {xs:'2px 10px', sm:'2px 10px'},
                                                            fontWeight: 500,
                                                            cursor: 'pointer',
                                                        }}
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            handleModuleCodeClick(module, dqco);
                                                        }}
                                                    >
                                                        {dqco}
                                                    </Typography>
                                                );
                                            })}

                                            <Typography sx={{
                                                backgroundColor: '#000000',
                                                color: '#ffffff',
                                                borderRadius: '20px',
                                                fontSize: {xs:'10px', sm:'16px'},
                                                padding: {xs:'2px 10px', sm:'2px 20px'},
                                                fontWeight: 500,
                                                textAlign:'center',
                                                cursor: 'pointer',
                                            }}
                                            onClick={e => {
                                                e.stopPropagation();
                                                handleModuleFrameworkAlignedClick(module);
                                            }}
                                            >
                                                Frameworks Aligned
                                            </Typography>
                                        </Box>

                                        {/* Framework Aligned Popup */}
                                        {frameAlignedContent && (
                                            <Box
                                                sx={{
                                                    position: 'fixed',
                                                    top: '50%',
                                                    left: '50%',
                                                    transform: 'translate(-50%, -50%)',
                                                    width: '60%',
                                                    maxWidth: '400px',
                                                    minWidth: '300px',
                                                    maxHeight: '70vh',
                                                    backgroundColor: 'background.paper',
                                                    border: '1px solid #000',
                                                    borderRadius: '20px',
                                                    boxShadow: 24,
                                                    p: 3,
                                                    zIndex: 1300,
                                                    overflow: 'hidden'
                                                }}
                                                onClick={(e) => {
                                                    if (e.target === e.currentTarget) {
                                                        setFrameAlignedContent(null);
                                                    }
                                                }}
                                            >
                                                <IconButton
                                                    aria-label="close"
                                                    onClick={() => setFrameAlignedContent(null)}
                                                    style={{
                                                        position: 'absolute',
                                                        top: 8,
                                                        right: 8,
                                                        color: '#000',
                                                        zIndex: 1
                                                    }}
                                                >
                                                    <Close />
                                                </IconButton>

                                                {/* Fixed Header */}
                                                <Box sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    mb: 2
                                                }}>
                                                    <Box sx={{
                                                        backgroundColor: '#000000',
                                                        color: 'white',
                                                        borderRadius: '20px',
                                                        px: 2,
                                                        py: 1,
                                                        fontWeight: 'medium',
                                                        fontSize: '14px'
                                                    }}>
                                                        Frameworks Aligned
                                                    </Box>
                                                </Box>

                                                {/* Scrollable Framework List */}
                                                <Box sx={{
                                                    width: '100%',
                                                    maxHeight: '50vh',
                                                    overflowY: 'auto',
                                                    overflowX: 'hidden',
                                                    pr: 1,
                                                    '&::-webkit-scrollbar': {
                                                        width: '6px',
                                                    },
                                                    '&::-webkit-scrollbar-track': {
                                                        background: '#f1f1f1',
                                                        borderRadius: '3px',
                                                    },
                                                    '&::-webkit-scrollbar-thumb': {
                                                        background: '#888',
                                                        borderRadius: '3px',
                                                    },
                                                    '&::-webkit-scrollbar-thumb:hover': {
                                                        background: '#555',
                                                    }
                                                }}>
                                                    {frameAlignedContent && frameAlignedContent.length > 0 ? (
                                                        <ul style={{
                                                            listStyleType: 'disc',
                                                            paddingLeft: '20px',
                                                            margin: 0,
                                                            fontSize: '14px'
                                                        }}>
                                                            {frameAlignedContent.map((framework, index) => (
                                                                <li key={index} style={{
                                                                    marginBottom: '8px',
                                                                    color: '#000',
                                                                    lineHeight: '1.4'
                                                                }}>
                                                                    {framework["SourceLink (same as ProgL2)"] ? (
                                                                        <a
                                                                            href={framework["SourceLink (same as ProgL2)"]}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                            style={{
                                                                                color: '#000000',
                                                                                textDecoration: 'underline',
                                                                                cursor: 'pointer',
                                                                                fontSize: '14px'
                                                                            }}
                                                                            onClick={(e) => e.stopPropagation()}
                                                                        >
                                                                            {framework.FrameName}
                                                                        </a>
                                                                    ) : (
                                                                        <span style={{
                                                                            color: '#000',
                                                                            fontSize: '14px'
                                                                        }}>
                                                                            {framework.FrameName}
                                                                        </span>
                                                                    )}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    ) : (
                                                        <Typography style={{
                                                            textAlign: 'center',
                                                            color: '#666',
                                                            fontStyle: 'italic',
                                                            fontSize: '14px',
                                                            padding: '20px 0'
                                                        }}>
                                                            No frameworks aligned for this module
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </Box>
                                        )}


                                        {popupContent && (
                                            <Box
                                                sx={{
                                                    position: 'fixed',
                                                    top: '50%',
                                                    left: '50%',
                                                    transform: 'translate(-50%, -50%)',
                                                    width: '80%',
                                                    maxWidth: '500px',
                                                    backgroundColor: 'background.paper',
                                                    border: '1px solid #000',
                                                    borderRadius: '20px',
                                                    boxShadow: 24,
                                                    p: 4,
                                                    zIndex: 1300,
                                                }}
                                                onClick={() => setPopupContent(null)}
                                            >
                                                <IconButton
                                                    aria-label="close"
                                                    onClick={() => setPopupContent(null)}
                                                    style={{
                                                        position: 'absolute',
                                                        top: 8,
                                                        right: 0,
                                                        color: '#000',
                                                    }}
                                                >
                                                    <Close />
                                                </IconButton>
                                                <Box sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    gap: 2
                                                }}>
                                                    <Box sx={{
                                                        backgroundColor: DQC_COLORS[popupContent.dqc_no] || '#D2DD2C',
                                                        color: 'white',
                                                        borderRadius: '20px',
                                                        px: 2,
                                                        py: 1,
                                                        fontWeight: 'medium',
                                                        mb: 2
                                                    }}>
                                                        {popupContent.code}
                                                    </Box>

                                                    {/* Title */}
                                                    <Typography variant="h5" component="h2" style={{
                                                        fontWeight: 'bold',
                                                        textAlign: 'center',
                                                        color: DQC_COLORS[popupContent.dqc_no] || '#D2DD2C',
                                                    }}>
                                                        {popupContent.title}
                                                    </Typography>

                                                    {/* Description */}
                                                    <Typography style={{
                                                        mt: 2,
                                                        whiteSpace: 'pre-line',
                                                        textAlign: 'left',
                                                        fontSize:'11px',
                                                        color: '#000'
                                                    }}>
                                                        {popupContent.description}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        )}
                                    </div>
                                    <Collapse in={expandedModules.includes(moduleIndex)}>
                                        <Typography variant="body1" style={{
                                            marginLeft: '8px',
                                            padding: '8px 0',
                                            width: '100%',
                                            color: '#606060'
                                        }}>
                                            {isEducatorView ? module.module_educator_description : module.module_learner_description}
                                        </Typography>
                                    </Collapse>
                                </div>

                                <Collapse in={expandedModules.includes(moduleIndex)} timeout="auto" unmountOnExit>
                                    {module.units.map((unit, unitIndex) => (
                                        <div key={unit.unit_code} className={classes.unit}
                                             style={{ marginLeft: '38px', width: '96%', }}>
                                            <div style={{
                                                display: 'flex',
                                                alignItems: 'flex-start',
                                                flexDirection:'column',
                                                width: '100%',
                                                justifyContent: 'space-between'
                                            }}>
                                                <div style={{display: 'flex', alignItems: 'flex-start', width: '100%', justifyContent: 'space-between'}}>
                                                <Button
                                                    onClick={() => toggleUnit(unit.unit_code)}
                                                    style={{
                                                        color: '#e7456d',
                                                        fontWeight: 'bold',
                                                        outline: 'none',
                                                        boxShadow: 'none',
                                                        fontSize: '18px',
                                                        justifyContent: 'space-between',
                                                        textAlign: 'left',
                                                        textTransform:'none',
                                                    }}
                                                    fullWidth
                                                >
                                                    {`Unit ${unitIndex + 1}: ${unit.unit_title} (${unit.unit_duration} MINUTES TOTAL)`}
                                                    {expandedUnits.includes(unit.unit_code) ?(
                                                        <Tooltip title="Collapse to hide details">
                                                            <ArrowDropUp />
                                                        </Tooltip>
                                                    ) : (
                                                        <Tooltip title="Expand to view details">
                                                            <ArrowDropDown />
                                                        </Tooltip>
                                                    )}
                                                </Button>
                                                {!outlineStatus.generation_initiated && (
                                                        <Tooltip title="Remove this item">
                                                            <IconButton
                                                                onClick={() => handleRemoveItem('unit', module.module_code, unit.unit_code)}
                                                                style={{ color: '#EE6628', marginLeft: '10px' }}
                                                            >
                                                                <RemoveIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                )}
                                                </div>
                                                <Collapse in={expandedUnits.includes(unit.unit_code)}>
                                                    <Typography variant="body1" style={{
                                                        marginLeft: '8px',
                                                        padding: '8px 0',
                                                        width: '100%',
                                                        color: '#606060'
                                                    }}>
                                                        {isEducatorView ? unit.unit_educator_description : unit.unit_learner_description}
                                                    </Typography>
                                                </Collapse>
                                            </div>
                                            <Collapse in={expandedUnits.includes(unit.unit_code)} timeout="auto"
                                                      unmountOnExit>
                                                <List>
                                                    {unit.lessons && unit.lessons.map((session, sessionIndex) => (
                                                        <div key={sessionIndex}>
                                                            <div style={{
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                width: '100%',
                                                                justifyContent: 'space-between',
                                                                padding: '8px 16px'
                                                            }}>
                                                                <Button
                                                                    onClick={() => toggleSession(session.session_code)}
                                                                    style={{
                                                                        color: '#EE6628',
                                                                        fontWeight: 'bold',
                                                                        outline: 'none',
                                                                        boxShadow: 'none',
                                                                        fontSize: '18px',
                                                                        textAlign: 'left',
                                                                        justifyContent: 'space-between',
                                                                        display: 'flex',
                                                                        padding: 0,
                                                                        width: '100%',
                                                                        textTransform: 'none'
                                                                    }}
                                                                >
                                                                    <div style={{display:'flex', flexDirection:'row', alignItems:'center'}}>
                                                                        <Typography variant="h6" className={classes.lesson}
                                                                                    style={{ marginLeft: '10px', fontSize: '18px',textTransform:'none', }}>
                                                                            {`Session ${sessionIndex + 1}: ${session.lesson_title} (${session.lesson_duration} minutes)`}
                                                                        </Typography>

                                                                        {/* Session Tokens row */}
                                                                        <div style={{display: 'flex',alignItems:'center', gap: '6px', marginLeft: '12px'}}>
                                                                            <Box sx={{ display: 'flex', gap: 1, ml: 1 }}>
                                                                                {session.session_unique_ksab?.map((token, i) => (
                                                                                    <Box
                                                                                        key={i}
                                                                                        title={token}
                                                                                        sx={{
                                                                                            width: 18,
                                                                                            height: 18,
                                                                                            borderRadius: '50%',
                                                                                            backgroundColor: KSAB_COLOR_MAPPING[token] || '#CCCCCC',
                                                                                        }}
                                                                                    />
                                                                                ))}
                                                                            </Box>
                                                                        </div>
                                                                    </div>

                                                                    {expandedSessions[session.session_code] ? (
                                                                        <Tooltip title="Collapse to hide details">
                                                                            <ArrowDropUp style={{ color: '#EE6628' }} />
                                                                        </Tooltip>
                                                                    ) : (
                                                                          <Tooltip title="Expand to view details">
                                                                              <ArrowDropDown style={{ color: '#EE6628' }} />
                                                                          </Tooltip>
                                                                    )}
                                                                </Button>

                                                                {!outlineStatus.generation_initiated && (
                                                                        <Tooltip title="Remove this item">
                                                                            <IconButton
                                                                                onClick={(e) => {
                                                                                    e.stopPropagation();
                                                                                    handleRemoveItem('session', module.module_code, unit.unit_code, session.session_code);
                                                                                }}
                                                                                style={{ color: '#EE6628', marginLeft: '10px' }}
                                                                            >
                                                                                <RemoveIcon />
                                                                            </IconButton>
                                                                        </Tooltip>
                                                                )}
                                                            </div>

                                                            <Collapse in={expandedSessions[session.session_code]}>
                                                                <Typography variant="body1" style={{
                                                                    marginLeft: '8px',
                                                                    padding: '8px 0 8px 20px',
                                                                    color: '#606060'
                                                                }}>
                                                                    {isEducatorView ? session.lesson_educator_description : session.lesson_learner_description}
                                                                </Typography>
                                                            </Collapse>

                                                            <Collapse in={expandedSessions[session.session_code]}
                                                                      timeout="auto" unmountOnExit>
                                                                {session.session_learning_objectives && (
                                                                    <List>
                                                                        <ListItem style={{ paddingTop: '0px' }}>
                                                                            <Typography variant="h6" style={{
                                                                                color: '#606060',
                                                                                marginLeft: '27px',
                                                                                fontSize: '17px',
                                                                            }}>
                                                                                Session Learning Objectives:
                                                                            </Typography>
                                                                        </ListItem>
                                                                        <ul style={{
                                                                            marginLeft: '40px',
                                                                            listStyleType: 'disc'
                                                                        }}>
                                                                            {session.session_learning_objectives.map((slo, sloIndex) => (
                                                                                <li key={sloIndex} style={{
                                                                                    marginBottom: '10px',
                                                                                    color: '#606060'
                                                                                }}>
                                                                                    {slo.description_for_educator}
                                                                                </li>
                                                                            ))}
                                                                        </ul>
                                                                    </List>
                                                                )}
                                                            </Collapse>
                                                        </div>
                                                    ))}
                                                </List>
                                            </Collapse>
                                        </div>
                                    ))}
                                </Collapse>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Updated Button Container */}
                <div className={classes.buttonContainer}>
                    {!outlineStatus.generation_initiated && (
                        <Button
                            variant="contained"
                            onClick={() => setOpenStartOverDialog(true)}
                            style={{
                                backgroundColor: '#0097b2',
                                color: 'white',
                                margin: '0 10px',
                                padding: '10px 30px',
                                borderRadius: '8px',
                                textTransform: 'none',
                                fontWeight: 'bold',
                                fontSize: '16px'
                            }}
                        >
                            Start Over
                        </Button>
                    )}
                    {!outlineStatus.generation_initiated && (
                        <Button
                        variant="contained"
                        onClick={() => {
                            if (outlineStatus.generation_initiated) {
                                history.push(`/dq_pro/products/program/dashboard/${dq_module_code}`);
                            } else {
                                handleGenerateClick();
                            }
                        }}
                        disabled={outlineStatus.generation_initiated && false}
                        style={{
                            backgroundColor: outlineStatus.generation_initiated ? '#d3d3d3' : '#ff6468',
                            color: 'white',
                            margin: '0 10px',
                            padding: '10px 30px',
                            borderRadius: '8px',
                            textTransform: 'none',
                            fontWeight: 'bold',
                            fontSize: '16px'
                        }}
                    >
                            Generate Program
                    </Button>
                    )}
                </div>
            </Box>
            <LoaderWithBackDrop loading={loading} />

            <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm" PaperProps={{ sx:{ border:'2px solid #000', borderRadius:'15px' } }}>
                <Box sx={{ p:4, position:'relative' }}>
                    <IconButton
                        aria-label="close"
                        onClick={handleCloseDialog}
                        sx={{ position:'absolute', right:8, top:8, color:'#999' }}
                    >
                        <Close />
                    </IconButton>
                    <Typography variant="h6" sx={{ mt:2, mb:4, textAlign:'center', color:'#ff5757' }}>
                        Are you sure you want to remove this {itemToRemove.type}?
                    </Typography>
                    <Box sx={{ display:'flex', flexDirection:{ xs:'column', sm:'row'}, alignItems:'center', justifyContent:'center', gap:2, width:'100%', mt:2 }}>
                        <Button
                            variant="contained"
                            sx={{ width:{ xs:'100%', sm:'40%'}, fontSize:'15px !important', fontWeight:'bold', backgroundColor:'#ff5757', '&:hover':{ backgroundColor:'#ff5757' } }}
                            onClick={handleConfirmRemove}
                        >
                            Yes, Remove
                        </Button>
                        <Button
                            variant="outlined"
                            sx={{ width:{ xs:'100%', sm:'40%'}, fontSize:'15px !important', borderColor:'#ff5757', color:'#ff5757', fontWeight:'bold' }}
                            onClick={handleCloseDialog}
                        >
                            Cancel
                        </Button>
                    </Box>
                </Box>
            </Dialog>
            <Dialog open={openStartOverDialog} onClose={() => setOpenStartOverDialog(false)} fullWidth maxWidth="sm" PaperProps={{ sx:{ border:'2px solid #000', borderRadius:'15px' } }}>
                <Box sx={{ p:4, position:'relative' }}>
                    <IconButton
                        aria-label="close"
                        onClick={() => setOpenStartOverDialog(false)}
                        sx={{ position:'absolute', right:8, top:8, color:'#999' }}
                    >
                        <Close />
                    </IconButton>
                    <Typography variant="h6" sx={{ mt:2, mb:4, textAlign:'center', color:'#ff5757' }}>
                        Are you sure you want to remove this program?<br/>This action cannot be undone.
                    </Typography>
                    <Box sx={{ display:'flex', flexDirection:{ xs:'column', sm:'row'}, alignItems:'center', justifyContent:'center', gap:2, width:'100%', mt:2 }}>
                        <Button
                            variant="contained"
                            sx={{ width:{ xs:'100%', sm:'40%'}, fontSize:'15px !important', fontWeight:'bold', backgroundColor:'#ff5757', '&:hover':{ backgroundColor:'#ff5757' } }}
                            onClick={() => { setOpenStartOverDialog(false); handleStartOver(); }}
                        >
                            Yes, Remove
                        </Button>
                        <Button
                            variant="outlined"
                            sx={{ width:{ xs:'100%', sm:'40%'}, fontSize:'15px !important', borderColor:'#ff5757', color:'#ff5757', fontWeight:'bold' }}
                            onClick={() => setOpenStartOverDialog(false)}
                        >
                            Cancel
                        </Button>
                    </Box>
                </Box>
            </Dialog>

            {/* Last Item Confirmation Dialog */}
            <Dialog open={openLastItemDialog} onClose={() => setOpenLastItemDialog(false)} fullWidth maxWidth="sm" PaperProps={{ sx:{ border:'2px solid #000', borderRadius:'15px' } }}>
                <Box sx={{ p:4, position:'relative' }}>
                    <IconButton
                        aria-label="close"
                        onClick={() => setOpenLastItemDialog(false)}
                        sx={{ position:'absolute', right:8, top:8, color:'#999' }}
                    >
                        <Close />
                    </IconButton>
                    <Typography variant="h6" sx={{ mt:2, mb:2, textAlign:'center', color:'#ff5757' }}>
                        {willDeleteParent === 'program'
                            ? 'Warning: Delete Current Program'
                            : `Warning: Delete ${willDeleteParent === 'module' ? 'Module' : 'Unit'}`
                        }
                    </Typography>
                    <Typography variant="body1" sx={{ textAlign:'center', mb:4, color:'#333' }}>
                        {willDeleteParent === 'program' ? (
                            <>
                                This is the last {lastItemToRemove.type} in the program.<br/>
                                Removing this will <strong>delete the entire current program</strong> and create a new program.<br/>
                                Are you sure you want to proceed? This action cannot be undone.
                            </>
                        ) : willDeleteParent === 'module' ? (
                            <>
                                This is the last {lastItemToRemove.type} in this module.<br/>
                                Removing this {lastItemToRemove.type} will also <strong>delete the entire module</strong>.<br/>
                                Are you sure you want to proceed? This action cannot be undone.
                            </>
                        ) : (
                            <>
                                This is the last {lastItemToRemove.type} in this unit.<br/>
                                Removing this {lastItemToRemove.type} will also <strong>delete the entire unit</strong>.<br/>
                                Are you sure you want to proceed? This action cannot be undone.
                            </>
                        )}
                    </Typography>
                    <Box sx={{ display:'flex', flexDirection:{ xs:'column', sm:'row'}, alignItems:'center', justifyContent:'center', gap:2, width:'100%', mt:2 }}>
                        <Button
                            variant="contained"
                            sx={{ width:{ xs:'100%', sm:'40%'}, fontSize:'15px !important', fontWeight:'bold', backgroundColor:'#ff5757', '&:hover':{ backgroundColor:'#ff5757' } }}
                            onClick={async () => {
                                await handleConfirmLastItemRemoval();
                            }}
                        >
                            {willDeleteParent === 'program'
                                ? 'Yes, Delete Program'
                                : `Yes, Delete ${willDeleteParent === 'module' ? 'Module' : 'Unit'}`
                            }
                        </Button>
                        <Button
                            variant="outlined"
                            sx={{ width:{ xs:'100%', sm:'40%'}, fontSize:'15px !important', borderColor:'#ff5757', color:'#ff5757', fontWeight:'bold' }}
                            onClick={() => setOpenLastItemDialog(false)}
                        >
                            Cancel
                        </Button>
                    </Box>
                </Box>
            </Dialog>

            {/* Generate Program Confirmation Dialog */}
            <Dialog 
                open={openGenerateConfirmDialog} 
                onClose={() => setOpenGenerateConfirmDialog(false)} 
                fullWidth 
                maxWidth="sm" 
                PaperProps={{ 
                    sx: { 
                        border: '1px solid #000', 
                        borderRadius: '13px',
                        textAlign: 'center'
                    } 
                }}
            >
                <Box sx={{ p: 4, position: 'relative' }}>
                    <Typography 
                        variant="body1" 
                        sx={{ 
                            mt: 2, 
                            mb: 4, 
                            textAlign: 'center',
                            fontSize: '16px',
                            lineHeight: 1.6
                        }}
                    >
                        Are you sure you'd like to generate this program with <b>{calculateTotalSessions()} session(s)?</b> <br/>
                        Once it's created, you won't be able to make changes, and you'll be billed for the generated session(s).
                    </Typography>
                    <Box 
                        sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', sm: 'row' }, 
                            alignItems: 'center', 
                            justifyContent: 'center', 
                            gap: 2, 
                            width: '100%', 
                            mt: 2 
                        }}
                    >
                        <Button
                            variant="outlined"
                            sx={{ 
                                width: { xs: '100%', sm: '30%' },
                                fontSize: '15px !important', 
                                fontWeight: 'bold', 
                                borderColor: '#0097b2',
                                borderRadius: '10px',
                                color: '#0097b2',
                                '&:hover': {
                                    borderColor: '#0097b2',
                                    backgroundColor: 'rgba(0, 151, 178, 0.1)'
                                }
                            }}
                            onClick={() => setOpenGenerateConfirmDialog(false)}
                        >
                            Go Back
                        </Button>
                        <Button
                            variant="contained"
                            sx={{ 
                                width: { xs: '100%', sm: '30%' },
                                fontSize: '15px !important', 
                                fontWeight: 'bold', 
                                backgroundColor: '#ff6468',
                                borderRadius: '10px',
                                '&:hover': { 
                                    backgroundColor: '#ff6468' 
                                } 
                            }}
                            onClick={() => {
                                setOpenGenerateConfirmDialog(false);
                                handleGenerateProgram();
                            }}
                        >
                            Yes, Generate
                        </Button>
                    </Box>
                </Box>
            </Dialog>
        </>
    )
};

export default DQProProgOutline;
